import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { DialogHeader, dialogActionsSx, dialogPaperProps } from "./DialogParts";

const IMAGE = /\.(png|jpe?g|webp)$/i;
// Spreadsheets are shown as a PDF copy (Excel) or as plain text (CSV), so
// columns and sheets may not look like they do in Excel.
const SPREADSHEET = /\.(xlsx|csv)$/i;

// Shows a resource's file inside the Trust Center page: PDFs and text files
// in a frame, images as images. Other files (Word, Excel) cannot be shown
// by the browser, so the viewer says so and offers Download.
// `link` is { url, fileName, viewable } from the API, null while loading.
const DocumentViewer = ({ resource, link, onDownload, onClose }) => {
  const theme = useTheme();
  const open = !!resource;
  const loading = open && !link;
  // The original file's name (the link may name its PDF copy).
  const originalName = resource?.fileName || link?.fileName || "";
  const spreadsheetNote =
    !loading && link?.viewable && SPREADSHEET.test(originalName);

  const body = () => {
    if (loading) {
      return (
        <Box
          sx={{ height: "100%", display: "grid", placeItems: "center" }}
          role="status"
          aria-label="Loading document"
        >
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (!link.viewable) {
      return (
        <Box
          sx={{
            height: "100%",
            display: "grid",
            placeItems: "center",
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            {link.fileName} cannot be shown here. Download it to open it.
          </Typography>
        </Box>
      );
    }
    if (IMAGE.test(link.fileName)) {
      return (
        <Box
          sx={{
            height: "100%",
            display: "grid",
            placeItems: "center",
            p: 2,
            overflow: "auto",
          }}
        >
          <Box
            component="img"
            src={link.url}
            alt={resource.name}
            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </Box>
      );
    }
    return (
      <Box
        component="iframe"
        src={link.url}
        title={resource.name}
        sx={{ width: "100%", height: "100%", border: 0, display: "block" }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        ...dialogPaperProps,
        sx: { ...dialogPaperProps.sx, height: "88vh" },
      }}
    >
      <DialogHeader
        title={resource?.name || ""}
        subtitle={link?.fileName}
        onClose={onClose}
      />
      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: 1,
          overflow: "hidden",
          backgroundColor: theme.customColors.background.light,
        }}
      >
        {open && (
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {spreadsheetNote && (
              <Alert severity="info" sx={{ borderRadius: 0, flexShrink: 0 }}>
                This is a preview of a spreadsheet, so columns and sheets may
                not look the same as in Excel. Download the file to see it
                exactly.
              </Alert>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>{body()}</Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ borderRadius: "8px", textTransform: "none" }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          disabled={loading}
          onClick={() => onDownload(resource)}
          sx={{ borderRadius: "8px", textTransform: "none" }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer;
